import { reactive } from 'vue'
import {
  createRouter,
  createWebHistory,
  type LocationQuery,
  type LocationQueryRaw,
  type LocationQueryValue,
  type Router,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from 'vue-router'
import { readStoredAuthSession } from '@/stores/auth.store'
import { hasAnyPermission } from '@/shared/helpers/user'
import { readStoredPublicSystemSettings } from '@/shared/helpers/system-setting-cache'
import { authRoutes } from './modules/auth.routes'
import { dashboardRoutes } from './modules/dashboard.routes'
import { legacyRoutes } from './modules/legacy.routes'
import { notFoundRoutes } from './modules/not-found.routes'
import { posRoutes } from './pos'
import { publicRoutes } from './modules/public.routes'

export const appRoutes: RouteRecordRaw[] = [
  ...dashboardRoutes,
  ...authRoutes,
  ...posRoutes,
  ...publicRoutes,
  ...legacyRoutes,
  ...notFoundRoutes,
]

export const DEFAULT_PAGE = 1
export const DEFAULT_ITEMS_PER_PAGE = 30
export const MIN_ITEMS_PER_PAGE = 10
export const MAX_ITEMS_PER_PAGE = 100
export const MAX_PAGE = 9999
export const PAGINATION_QUERY_KEYS = ['page', 'itemsPerPage', 'sortBy', 'sortDesc'] as const

type PaginationQueryKey = (typeof PAGINATION_QUERY_KEYS)[number]
type SortDirection = 'asc' | 'desc'

export interface RouteQueryState {
  currentPage: number
  itemsPerPage: number
  sortBy: string
  sortDesc: boolean
  customData: Record<string, string>
  browserNavigation: boolean
  navigationVersion: number
}

export interface ValidatedRouteQuery {
  page: number
  itemsPerPage: number
  sortBy: string | null
  sortDesc: SortDirection | null
  needsFix: boolean
}

export interface SetRouteQueryDataOptions {
  page?: number
  itemsPerPage?: number
  sortBy?: string | null
  sortDesc?: boolean | SortDirection | null
  customData?: Record<string, string | number | boolean | null | undefined>
}

export const routeQueryState = reactive<RouteQueryState>({
  currentPage: DEFAULT_PAGE,
  itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
  sortBy: '',
  sortDesc: false,
  customData: {},
  browserNavigation: false,
  navigationVersion: 0,
})

let browserNavigationListenerRegistered = false
let authStartupChecked = false

export function markAuthStartupChecked(checked = true) {
  authStartupChecked = checked
}

export function resetAuthStartupCheckForTests(checked = false) {
  authStartupChecked = checked
}

function isPaginationQueryKey(key: string): key is PaginationQueryKey {
  return PAGINATION_QUERY_KEYS.includes(key as PaginationQueryKey)
}

function getFirstQueryValue(value: LocationQueryValue | LocationQueryValue[] | unknown) {
  if (Array.isArray(value)) {
    return value[0] ?? null
  }

  return value ?? null
}

function normalizeNumberQueryParam(value: unknown) {
  const firstValue = getFirstQueryValue(value)

  if (firstValue === null || firstValue === '') {
    return null
  }

  const numberValue = Number(firstValue)

  return Number.isFinite(numberValue) ? numberValue : null
}

function clampNumber(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function normalizeStringQueryParam(value: unknown) {
  const firstValue = getFirstQueryValue(value)

  return typeof firstValue === 'string' && firstValue.length > 0 ? firstValue : null
}

function isQueryArray(value: unknown) {
  return Array.isArray(value)
}

export function extractCustomQueryData(query: LocationQuery | Record<string, unknown>) {
  const customData: Record<string, string> = {}

  Object.entries(query).forEach(([key, value]) => {
    if (isPaginationQueryKey(key)) {
      return
    }

    const firstValue = getFirstQueryValue(value)

    if (firstValue !== null && firstValue !== undefined) {
      customData[key] = String(firstValue)
    }
  })

  return customData
}

export function validateRouteQueryParams(
  query: LocationQuery | Record<string, unknown>,
): ValidatedRouteQuery {
  let needsFix = false
  const rawPage = normalizeNumberQueryParam(query.page)
  const rawItemsPerPage = normalizeNumberQueryParam(query.itemsPerPage)
  const page = clampNumber(rawPage ?? DEFAULT_PAGE, DEFAULT_PAGE, MAX_PAGE)
  const itemsPerPage = clampNumber(
    rawItemsPerPage ?? DEFAULT_ITEMS_PER_PAGE,
    MIN_ITEMS_PER_PAGE,
    MAX_ITEMS_PER_PAGE,
  )

  if (query.page != null && (rawPage === null || rawPage !== page || isQueryArray(query.page))) {
    needsFix = true
  }

  if (
    query.itemsPerPage != null &&
    (rawItemsPerPage === null ||
      rawItemsPerPage !== itemsPerPage ||
      isQueryArray(query.itemsPerPage))
  ) {
    needsFix = true
  }

  const sortBy = normalizeStringQueryParam(query.sortBy)

  if (query.sortBy != null && (sortBy === null || isQueryArray(query.sortBy))) {
    needsFix = true
  }

  const rawSortDesc = normalizeStringQueryParam(query.sortDesc)
  const sortDesc = rawSortDesc === 'desc' || rawSortDesc === 'asc' ? rawSortDesc : null

  if (query.sortDesc != null && (sortDesc === null || isQueryArray(query.sortDesc))) {
    needsFix = true
  }

  return {
    page,
    itemsPerPage,
    sortBy,
    sortDesc,
    needsFix,
  }
}

function buildNormalizedQuery(query: LocationQuery | Record<string, unknown>) {
  const validatedQuery = validateRouteQueryParams(query)
  const normalizedQuery: LocationQueryRaw = {
    ...extractCustomQueryData(query),
  }

  if (query.page != null) {
    normalizedQuery.page = String(validatedQuery.page)
  }

  if (query.itemsPerPage != null) {
    normalizedQuery.itemsPerPage = String(validatedQuery.itemsPerPage)
  }

  if (validatedQuery.sortBy) {
    normalizedQuery.sortBy = validatedQuery.sortBy
  }

  if (validatedQuery.sortDesc) {
    normalizedQuery.sortDesc = validatedQuery.sortDesc
  }

  return {
    query: normalizedQuery,
    validatedQuery,
  }
}

function updateRouteQueryState(route: RouteLocationNormalized) {
  const validatedQuery = validateRouteQueryParams(route.query)

  routeQueryState.currentPage = validatedQuery.page
  routeQueryState.itemsPerPage = validatedQuery.itemsPerPage
  routeQueryState.sortBy = validatedQuery.sortBy ?? ''
  routeQueryState.sortDesc = validatedQuery.sortDesc === 'desc'
  routeQueryState.customData = extractCustomQueryData(route.query)
  routeQueryState.navigationVersion += 1
}

export function markBrowserNavigation() {
  routeQueryState.browserNavigation = true
}

export async function setRouteQueryData(router: Router, data: SetRouteQueryDataOptions) {
  const currentQuery = extractCustomQueryData(router.currentRoute.value.query)
  const nextQuery: LocationQueryRaw = {
    ...currentQuery,
  }

  Object.entries(data.customData ?? {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined && !isPaginationQueryKey(key)) {
      nextQuery[key] = String(value)
    }
  })

  const page = data.page ?? routeQueryState.currentPage
  const itemsPerPage = data.itemsPerPage ?? routeQueryState.itemsPerPage
  const sortBy = data.sortBy ?? routeQueryState.sortBy
  const sortDesc = data.sortDesc ?? routeQueryState.sortDesc

  nextQuery.page = String(clampNumber(page, DEFAULT_PAGE, MAX_PAGE))
  nextQuery.itemsPerPage = String(clampNumber(itemsPerPage, MIN_ITEMS_PER_PAGE, MAX_ITEMS_PER_PAGE))

  if (sortBy) {
    nextQuery.sortBy = sortBy
  }

  if (sortDesc) {
    nextQuery.sortDesc = sortDesc === true || sortDesc === 'desc' ? 'desc' : 'asc'
  }

  await router.replace({
    path: router.currentRoute.value.path,
    query: nextQuery,
  })
}

export function registerRouterGuards(router: Router) {
  if (typeof window !== 'undefined' && !browserNavigationListenerRegistered) {
    window.addEventListener('popstate', markBrowserNavigation)
    browserNavigationListenerRegistered = true
  }

  router.beforeEach((to) => {
    const session = readStoredAuthSession()
    const hasSession = Boolean(session)
    const routeName = typeof to.name === 'string' ? to.name : null
    const isChangePasswordRoute = routeName === 'change-password'
    const canSkipStartupCheck =
      to.meta.public === true ||
      routeName === 'splash' ||
      ((routeName === 'login' || routeName === 'forgot-password') && !hasSession)

    if (!authStartupChecked && !canSkipStartupCheck) {
      return {
        name: 'splash',
        query: {
          redirect: to.fullPath,
        },
      }
    }

    if (to.meta.requiresAuth && !hasSession) {
      return {
        name: 'login',
        query: {
          redirect: to.fullPath,
        },
      }
    }

    if (
      to.meta.requiresAuth &&
      hasSession &&
      session?.user.must_change_password === true &&
      !isChangePasswordRoute
    ) {
      return {
        name: 'change-password',
        query: {
          redirect: to.fullPath,
        },
      }
    }

    if (
      to.meta.requiresKitchen === true &&
      readStoredPublicSystemSettings()?.sale_settings.kitchen_enabled === false
    ) {
      return { name: 'pos-table-sales', query: { kitchen: 'disabled' } }
    }

    if (to.meta.guestOnly && hasSession) {
      return { name: 'home' }
    }

    const routePermissions = Array.isArray(to.meta.permissions) ? to.meta.permissions : []

    if (
      to.meta.requiresAuth &&
      routePermissions.length > 0 &&
      !hasAnyPermission(session, routePermissions) &&
      routeName !== 'forbidden'
    ) {
      return {
        name: 'forbidden',
        query: {
          redirect: to.fullPath,
        },
      }
    }

    if (to.meta.requiresAuth) {
      const { query, validatedQuery } = buildNormalizedQuery(to.query)

      if (validatedQuery.needsFix) {
        return {
          path: to.path,
          query,
          hash: to.hash,
          replace: true,
        }
      }
    }
  })

  router.afterEach((to) => {
    if (to.meta.requiresAuth) {
      updateRouteQueryState(to)
    }
  })
}

export function createAppRouter() {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: appRoutes,
  })

  registerRouterGuards(router)

  return router
}

const router = createAppRouter()

export default router
